import React from "react";
import { MdAccountBalanceWallet } from "react-icons/md";
import Card from "../../../../components/card";
import { ReceivablesKpi } from "../../../../interfaces/Dashboard";

/**
 * Funil de recebíveis por fase (Pendente de NF → Faturada → Recebida).
 *
 * Barras em CSS puro, sem biblioteca de gráfico: são 3 valores comparáveis, e trazer 500 KB de
 * ApexCharts para desenhar 3 retângulos não se paga.
 */

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const ReceivablesFunnelCard: React.FC<{ data: ReceivablesKpi }> = ({ data }) => {
  const max = Math.max(...data.funnel.map((f) => f.amount), 1);

  return (
    <Card extra="p-5">
      <div className="mb-1 flex items-center gap-2">
        <MdAccountBalanceWallet className="h-5 w-5 text-dv-blue-500 dark:text-dv-cyan-300" aria-hidden="true" />
        <h3 className="text-base font-bold text-navy-700 dark:text-white">A receber</h3>
      </div>
      <p className="mb-4 text-xs text-gray-700 dark:text-gray-300">
        Tudo que ainda vai entrar, de qualquer competência — um recebível antigo em aberto é o que
        mais importa aqui.
      </p>

      {data.funnel.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Nenhuma cobrança em aberto.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {data.funnel.map((stage) => (
            <div key={stage.phase}>
              <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                <span className="truncate font-medium text-navy-700 dark:text-white">
                  {stage.phaseName}
                  <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400">
                    · {stage.count}
                  </span>
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-navy-700 dark:text-white">
                  {fmtMoney(stage.amount)}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-dv-blue-500 to-dv-cyan-400 transition-all duration-500"
                  style={{ width: `${(stage.amount / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs dark:border-white/10">
        <span className="text-gray-700 dark:text-gray-300">
          Recebido no mês:{" "}
          <strong className="tabular-nums text-navy-700 dark:text-white">
            {fmtMoney(data.receivedThisMonth)}
          </strong>
        </span>
        {data.oldestOpenDays != null && (
          <span className="text-gray-700 dark:text-gray-300">
            Mais antigo em aberto:{" "}
            <strong className="tabular-nums text-navy-700 dark:text-white">
              {data.oldestOpenDays} dias
            </strong>
          </span>
        )}
      </div>
    </Card>
  );
};

export default ReceivablesFunnelCard;
